// src/views/UserViews/insights/components/FeatureComplianceSection.tsx
//
// Three flat (borderless) MetricCards: arc (impact sensing), fall detection, and head impact
// per-feature compliance scores, sourced from `getFeatureCompliance` (InsightsDAO). Rendered
// directly under the compliance trend chart (Insights.tsx).
//
// Four rules encoded by the cards below (see `getFeatureCompliance`'s header comment for
// the DAO-side half of this contract):
//  1. Each card shows `1 - fail_ratio` (already inverted by the DAO) as a "compliance
//     score", never the raw fail ratio - a higher number here means more compliant.
//  2. Non-additive - arc/fall/head are three independent diagnostics. This section never
//     renders a fourth "total" card, and no card's value is derived from the other two.
//  3. Head impact: a Core-only org has zero `head_applicable_seconds` for the whole window,
//     so `headScore` is null by construction (see `getFeatureCompliance`'s own comment).
//     This null state renders with the generic "not enough data" caption, the same as every
//     other null metric on this page - it no longer gets a dedicated "Pro devices only"
//     caption (see the inline comment at the row below for why).
//  4. The subcaption below the title says "of applicable reporting time", never "of all
//     time" or anything implying these three ratios partition the window - each is scoped
//     to the minutes its own feature was actually required for the device class.
//
// Copy avoids "compliance leaderboard", "worn", "session", or any per-person framing - this
// remains an aggregate, org-level, diagnostic measure only.
//
// Restyle (stacked rows): three label-left/value-right rows, one per line, with no vertical
// dividers - each row is a `FeatureComplianceRow`, not a `MetricCard` in its own flex column.
// The per-item "of applicable reporting time" wording lives once, at the section level, in
// the subcaption below (see Rule 4) and in each row's tooltip - repeating it three times
// would clutter a layout whose whole point is a compact list, and the section-level
// subcaption already says it for all three.
//
// Value color (superseded below - kept per NFR-010, not deleted): all three percentages
// used to render in one consistent, non-conditional color - `FeatureComplianceRow` had no
// color-override prop, so every row used its own fixed `primary.main`, the same color every
// other undecorated MetricCard on this page uses (e.g. FleetStatusSection's cards,
// SafetySection's "protected reporting time"/"reporting coverage"). This section previously
// tinted a score orange below a provisional 90% threshold, but that cutoff was never a real
// product-defined target, and tying color to an arbitrary number risked reading as more
// authoritative than it was - so the threshold and its color logic were removed rather than
// kept and refined.
//
// Value color, reinstated (explicit product request, 2026-09-04): per-row color is back via
// `valueColorForScore` and `FeatureComplianceRow`'s `valueColor` prop, below. This is
// deliberately NOT a return to the single-magic-number cutoff the paragraph above describes
// - the objection that removed it (an arbitrary threshold reading as more authoritative than
// it was) doesn't apply the same way to the new scheme, because the new scheme isn't invented
// fresh for this section: it's the exact same even-thirds pattern `UtilizationSection.tsx`'s
// `valueColorForMuteRatio` already uses for "Time muted" on this same page (bottom/middle/top
// third, not a single hand-picked percentage), just with the comparison direction inverted -
// a compliance score is high-is-good (higher percentage the better), where a mute ratio is
// high-is-bad. Reusing an already-principled, page-wide pattern is a materially different
// justification than picking one section's own arbitrary number, which is why this reverses
// cleanly without contradicting the reasoning above rather than just overwriting it.
//
// Title size (moderate bump, not a restyle): the title now carries an explicit
// SECTION_TITLE_FONT_SIZE below instead of relying on subtitle1's bare 16px default - a
// "bit bigger" pass only, independent of the rest of this still-partly-stale header comment.

import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import type { InsightsSectionState } from '@/hooks/insights/useInsightsData';
import type { FeatureComplianceSummary } from '@/models/Insights/InsightsTypes';
import FeatureComplianceRow from './FeatureComplianceRow';

// 18px: a moderate bump off subtitle1's default 1rem/16px (kept as the base variant for its
// weight/letter-spacing/line-height), sized to read as a section heading above the three
// now-larger FeatureComplianceRow values without doubling in size. Independent of the parent
// trend card's own 16px SectionCard title (see this task's report re: that hierarchy flag).
const SECTION_TITLE_FONT_SIZE = '1.125rem'; // 18px

// Even thirds (0-1/3 / 1/3-2/3 / 2/3-1), mirroring UtilizationSection.tsx's
// TIME_MUTED_WARNING_THRESHOLD/TIME_MUTED_ERROR_THRESHOLD pattern verbatim in magnitude -
// not a newly-invented cutoff (see the "Value color, reinstated" header paragraph above for
// the full reversal rationale). `FEATURE_COMPLIANCE_WARNING_THRESHOLD` keeps the name
// UtilizationSection.tsx's own header comment already forward-references for this exact
// constant. Naming ERROR on the numerically SMALLER cutoff and WARNING on the LARGER one is
// the opposite of mute's assignment (WARNING=1/3, ERROR=2/3) - intentional, not a mistake:
// polarity is inverted here (low is bad for a compliance score, not high), so the worse
// (error) band sits below the smaller threshold rather than above the larger one.
const FEATURE_COMPLIANCE_ERROR_THRESHOLD = 1 / 3;
const FEATURE_COMPLIANCE_WARNING_THRESHOLD = 2 / 3;

/** Mirrors `UtilizationSection.tsx`'s `valueColorForMuteRatio` (see that function's own
 * comment) with the comparison direction inverted: a compliance score is high-is-good, so
 * the bottom third is the one worth flagging red, not the top. `null` is never colored - it
 * always renders `FeatureComplianceRow`'s own uncolored em-dash treatment (Rule 1), the same
 * guarantee `valueColorForMuteRatio` gives its own null case.
 *
 * Boundary values resolve the same numeric direction mute's own `>=`-anchored checks do -
 * a tie always lands in the band that *starts* at that exact value moving upward, regardless
 * of which band is "more severe": exactly `1/3` is NOT the error band (it's warning, the
 * band starting at 1/3), and exactly `2/3` is NOT the warning band (it's the top band,
 * starting at 2/3) - see this file's tests for the explicit boundary cases.
 *
 * Top-band choice (judgment call, see this task's report for the full (a)-vs-(b)
 * discussion): unlike mute's own top/good band (`undefined`, no color at all), a good
 * compliance score here keeps the fixed `primary.main` every other undecorated MetricCard on
 * this page already uses when nothing needs flagging - this changes less, and only
 * introduces new color where there's something to flag (the two lower bands), rather than
 * also stripping color from the page's already-established "everything is fine" baseline. */
export function valueColorForScore(ratio: number | null): string | undefined {
  if (ratio === null) return undefined;
  if (ratio < FEATURE_COMPLIANCE_ERROR_THRESHOLD) return 'error.main';
  if (ratio < FEATURE_COMPLIANCE_WARNING_THRESHOLD) return 'warning.main';
  return 'primary.main';
}

type FeatureComplianceSectionProps = {
  featureCompliance: InsightsSectionState<FeatureComplianceSummary>;
};

export default function FeatureComplianceSection({
  featureCompliance,
}: FeatureComplianceSectionProps) {
  const { t } = useTranslation();
  const { data, loading, error } = featureCompliance;

  return (
    <Box>
      <Typography
        variant="subtitle1"
        component="h2"
        sx={{ fontWeight: 700, fontSize: SECTION_TITLE_FONT_SIZE }}
      >
        {t(StringKeys.INSIGHTS_FEATURE_COMPLIANCE_SECTION_TITLE)}
      </Typography>
      <Typography
        variant="caption"
        component="p"
        color="text.secondary"
        sx={{ mb: 2, display: 'block' }}
      >
        {t(StringKeys.INSIGHTS_FEATURE_COMPLIANCE_SUBCAPTION)}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <FeatureComplianceRow
          testId="feature-compliance-arc"
          label={t(StringKeys.INSIGHTS_ARC_COMPLIANCE_LABEL)}
          tooltip={t(StringKeys.INSIGHTS_ARC_COMPLIANCE_TOOLTIP)}
          value={data?.arcScore ?? null}
          valueColor={valueColorForScore(data?.arcScore ?? null)}
          loading={loading}
          error={error}
        />
        <FeatureComplianceRow
          testId="feature-compliance-fall"
          label={t(StringKeys.INSIGHTS_FALL_COMPLIANCE_LABEL)}
          tooltip={t(StringKeys.INSIGHTS_FALL_COMPLIANCE_TOOLTIP)}
          value={data?.fallScore ?? null}
          valueColor={valueColorForScore(data?.fallScore ?? null)}
          loading={loading}
          error={error}
        />
        {/* Rule 3: a Core-only org has `headScore === null` by construction (zero
            head_applicable_seconds for the whole window), so this must never render as 0%.
            No `nullCaption` override here (deliberately, no longer the Pro-only string):
            the underlying data still nulls head-impact out for Core devices for a reason
            that has nothing to do with "Pro-only" - the SQL/Cube gate behind
            `head_applicable_seconds` hasn't been corrected yet (tracked in
            software-data-analytics's docs/superpowers/plans/
            2026-09-03-head-impact-core-pro-correction.md, not yet executed), and this
            project no longer believes "Pro-only" is an accurate claim to assert in the UI
            (per a product/hardware correction). Asserting the old Pro-only caption would
            assert something now believed false; inventing a new claim the data doesn't yet
            support would be just as wrong. Falling back to `FeatureComplianceRow`'s default
            `INSIGHTS_METRIC_NOT_ENOUGH_DATA` avoids both. `INSIGHTS_HEAD_IMPACT_NOT_APPLICABLE`
            itself is intentionally left defined (StringKeys.js + both locale files) but
            unused, so restoring it here is a one-line change once that data pipeline
            correction ships and head-impact nulls for Core devices stop being a data gap. */}
        <FeatureComplianceRow
          testId="feature-compliance-head"
          label={t(StringKeys.INSIGHTS_HEAD_COMPLIANCE_LABEL)}
          tooltip={t(StringKeys.INSIGHTS_HEAD_COMPLIANCE_TOOLTIP)}
          value={data?.headScore ?? null}
          valueColor={valueColorForScore(data?.headScore ?? null)}
          loading={loading}
          error={error}
        />
      </Box>
    </Box>
  );
}
